import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { entAxios } from "./entApi";
import { money, downloadPdf } from "./api";
import { toast } from "sonner";
import { LogOut, Bike, RefreshCw, Navigation, Check, PackageCheck, CreditCard, Route, History, FileText, ChevronDown, ChevronUp } from "lucide-react";

export default function EntregadorDashboardPage() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [fila, setFila] = useState({ disponiveis: [], minhas: [] });
  const [hist, setHist] = useState([]);
  const [eta, setEta] = useState({});
  const [tab, setTab] = useState("ativas"); // ativas | disponiveis | historico
  const [expanded, setExpanded] = useState({}); // pedido_id → boolean

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const baixarPDF = (p) => {
    downloadPdf(`/vaipede/entregador/pedidos/${p.id}/recibo-termico`, `pedido-${p.numero}.pdf`, "vaipede_entregador_token")
      .catch(() => toast.error("Erro ao gerar PDF"));
  };

  useEffect(() => {
    try { setMe(JSON.parse(localStorage.getItem("vaipede_entregador") || "null")); } catch {}
    if (!localStorage.getItem("vaipede_entregador_token")) {
      nav("/vaipede/entregador/login", { replace: true });
      return;
    }
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (tab === "historico") loadHist(); }, [tab]);

  const load = async () => {
    try {
      const { data } = await entAxios.get("/vaipede/entregador/fila");
      setFila({ disponiveis: data.disponiveis || [], minhas: data.minhas || [] });
    } catch {}
  };
  const loadHist = async () => {
    try {
      const { data } = await entAxios.get("/vaipede/entregador/historico");
      setHist(data);
    } catch {}
  };

  const pegar = async (p) => {
    try { await entAxios.post(`/vaipede/entregador/pegar/${p.id}`); toast.success(`Você pegou #${p.numero}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Já pego"); load(); }
  };

  const sair = async (p) => {
    const et = parseInt(eta[p.id] || 20);
    try {
      const { data } = await entAxios.post(`/vaipede/entregador/saiu/${p.id}`, { eta_minutos: et });
      toast.success(`Rota iniciada #${p.numero}`);
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
      load();
    } catch { toast.error("Erro"); }
  };

  const entregue = async (p) => {
    try { await entAxios.post(`/vaipede/entregador/entregue/${p.id}`); toast.success(`Entregue!`); load(); }
    catch { toast.error("Erro"); }
  };

  const checkoutPagto = async (p) => {
    try {
      const { data } = await entAxios.post(`/vaipede/entregador/checkout/${p.id}`, {});
      toast.success("Link de pagamento gerado — abra para o cliente");
      window.open(data.checkout_url, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro checkout"); }
  };

  const irAgora = (p) => {
    if (p.latitude && p.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`, "_blank");
    } else if (p.endereco) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(p.endereco)}`, "_blank");
    } else toast.info("Sem endereço");
  };

  const rotaOtimizada = () => {
    const pedidos = fila.minhas.filter(p => (p.latitude && p.longitude) || p.endereco);
    if (pedidos.length < 2) return toast.info("Precisa de 2+ entregas para otimizar");
    // O Google Maps abre com waypoints — a última é o destination, as anteriores são waypoints
    const encode = (p) => (p.latitude && p.longitude) ? `${p.latitude},${p.longitude}` : encodeURIComponent(p.endereco);
    const destino = encode(pedidos[pedidos.length - 1]);
    const wps = pedidos.slice(0, -1).map(encode).join("|");
    // travelmode=driving; waypoints suporta multi-stops otimizados no cliente
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destino}&waypoints=${wps}&travelmode=driving`;
    window.open(url, "_blank");
  };

  const logout = () => {
    localStorage.removeItem("vaipede_entregador_token");
    localStorage.removeItem("vaipede_entregador");
    nav("/vaipede/entregador/login");
  };

  const TABS = [
    { k: "ativas", l: `Minhas (${fila.minhas.length})` },
    { k: "disponiveis", l: `Disponíveis (${fila.disponiveis.length})` },
    { k: "historico", l: "Histórico" },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 pb-8" data-testid="vaipede-ent-painel">
      <header className="bg-neutral-900 text-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#EA1D2C] grid place-items-center"><Bike className="w-5 h-5"/></div>
            <div>
              <div className="font-black">{me?.nome || "Entregador"}</div>
              <div className="text-xs text-neutral-400">{me?.whatsapp}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="p-2 rounded-xl bg-white/10"><RefreshCw className="w-4 h-4"/></button>
            <button onClick={logout} className="p-2 rounded-xl bg-white/10" data-testid="vaipede-ent-logout"><LogOut className="w-4 h-4"/></button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4">
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap ${tab === t.k ? "bg-[#EA1D2C] text-white" : "bg-white border border-neutral-200"}`} data-testid={`vaipede-ent-tab-${t.k}`}>
              {t.k === "historico" && <History className="w-3 h-3 inline mr-1"/>} {t.l}
            </button>
          ))}
          {tab === "ativas" && fila.minhas.length >= 2 && (
            <button onClick={rotaOtimizada} className="ml-auto px-4 py-2 rounded-full text-sm font-bold bg-blue-600 text-white whitespace-nowrap flex items-center gap-1" data-testid="vaipede-ent-rota-otim">
              <Route className="w-4 h-4"/> Rota otimizada
            </button>
          )}
        </div>

        {tab === "ativas" && (
          <div className="space-y-3">
            {fila.minhas.length === 0 && <p className="text-sm text-neutral-500">Nenhuma entrega ativa.</p>}
            {fila.minhas.map(p => (
              <div key={p.id} className="bg-white rounded-2xl border border-neutral-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-black">#{p.numero} · {p.cliente_nome}</div>
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${p.status === "saiu_entrega" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {p.status === "saiu_entrega" ? "🛵 A caminho" : "✅ Pronto"}
                  </span>
                </div>
                {p.endereco && <div className="text-xs text-neutral-600 mb-1">📍 {p.endereco}</div>}
                {p.referencia && <div className="text-xs text-neutral-600 mb-1">🧭 <b>Ref:</b> {p.referencia}</div>}
                {(p.taxa_entrega || 0) > 0 && <div className="text-xs text-neutral-600 mb-1">🛵 Taxa: {money(p.taxa_entrega)}{p.regiao_nome ? ` · ${p.regiao_nome}` : ""}</div>}
                <div className="text-sm mb-2">💰 {money(p.total)} · 💳 {p.forma_pagamento || "—"}</div>

                {/* Detalhes expansíveis do pedido */}
                <div className="mb-2">
                  <button onClick={() => toggleExpand(p.id)} className="text-xs text-blue-600 font-bold flex items-center gap-1" data-testid={`vaipede-ent-detalhes-${p.id}`}>
                    {expanded[p.id] ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
                    {expanded[p.id] ? "Esconder detalhes" : "Ver detalhes do pedido"}
                  </button>
                  {expanded[p.id] && (
                    <div className="mt-2 p-2 rounded-lg bg-neutral-50 text-xs space-y-0.5" data-testid={`vaipede-ent-info-${p.id}`}>
                      {(p.itens || []).map((it, i) => (
                        <div key={i} className="flex justify-between">
                          <span>{it.quantidade}× {it.nome}</span>
                          <b>{money((it.preco || 0) * (it.quantidade || 1))}</b>
                        </div>
                      ))}
                      {p.cliente_whatsapp && <div className="pt-1 mt-1 border-t border-neutral-200">📱 {p.cliente_whatsapp}</div>}
                      {p.observacao && <div>📝 {p.observacao}</div>}
                    </div>
                  )}
                </div>

                <button onClick={() => baixarPDF(p)} className="w-full mb-2 py-2 rounded-xl bg-neutral-900 text-white font-bold text-xs flex items-center justify-center gap-1" data-testid={`vaipede-ent-pdf-${p.id}`}>
                  <FileText className="w-3.5 h-3.5"/> Baixar PDF do pedido
                </button>

                {p.forma_pagamento === "dinheiro" && p.troco_para && Number(p.troco_para) > 0 && (
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-2 mb-2" data-testid={`vaipede-ent-troco-${p.id}`}>
                    <div className="text-xs font-black text-amber-800">💵 CLIENTE VAI PAGAR COM {money(p.troco_para)}</div>
                    <div className="text-sm font-black text-amber-900">→ LEVAR {money(Math.max(0, Number(p.troco_para) - Number(p.total || 0)))} DE TROCO</div>
                  </div>
                )}

                {p.forma_pagamento !== "infinitepay" && (
                  <button onClick={() => checkoutPagto(p)} className="w-full mb-2 py-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold text-sm flex items-center justify-center gap-2" data-testid={`vaipede-ent-checkout-${p.id}`}>
                    <CreditCard className="w-4 h-4"/> Gerar cobrança InfinitePay para receber
                  </button>
                )}

                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => irAgora(p)} className="py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm flex items-center justify-center gap-1" data-testid={`vaipede-ent-ir-${p.id}`}>
                    <Navigation className="w-4 h-4"/> Ir
                  </button>
                  {p.status !== "saiu_entrega" ? (
                    <>
                      <input type="number" min="1" value={eta[p.id] || 20} onChange={e => setEta({...eta, [p.id]: e.target.value})} className="text-center rounded-xl border border-neutral-200 text-sm"/>
                      <button onClick={() => sair(p)} className="py-2.5 rounded-xl bg-[#EA1D2C] text-white font-bold text-sm flex items-center justify-center gap-1" data-testid={`vaipede-ent-sair-${p.id}`}>
                        <Bike className="w-4 h-4"/> Saí
                      </button>
                    </>
                  ) : (
                    <button onClick={() => entregue(p)} className="col-span-2 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-1" data-testid={`vaipede-ent-entregue-${p.id}`}>
                      <PackageCheck className="w-4 h-4"/> Marcar entregue
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "disponiveis" && (
          <div className="space-y-3">
            {fila.disponiveis.length === 0 && <p className="text-sm text-neutral-500">Sem entregas disponíveis.</p>}
            {fila.disponiveis.map(p => (
              <div key={p.id} className="bg-white rounded-2xl border-2 border-amber-300 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-black">#{p.numero} · {p.cliente_nome}</div>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold">NOVO</span>
                </div>
                {p.endereco && <div className="text-xs text-neutral-600 mb-1">📍 {p.endereco}</div>}
                {p.referencia && <div className="text-xs text-neutral-600 mb-1">🧭 <b>Ref:</b> {p.referencia}</div>}
                <div className="text-sm mb-3">💰 {money(p.total)}{p.forma_pagamento ? ` · 💳 ${p.forma_pagamento}` : ""}</div>
                {p.forma_pagamento === "dinheiro" && p.troco_para && Number(p.troco_para) > 0 && (
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-2 mb-3" data-testid={`vaipede-ent-troco-disp-${p.id}`}>
                    <div className="text-xs font-black text-amber-800">💵 CLIENTE VAI PAGAR COM {money(p.troco_para)}</div>
                    <div className="text-sm font-black text-amber-900">→ LEVAR {money(Math.max(0, Number(p.troco_para) - Number(p.total || 0)))} DE TROCO</div>
                  </div>
                )}
                <button onClick={() => pegar(p)} className="w-full py-3 rounded-xl bg-emerald-600 text-white font-black flex items-center justify-center gap-2" data-testid={`vaipede-ent-pegar-${p.id}`}>
                  <Check className="w-4 h-4"/> Pegar esta entrega
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "historico" && (
          <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
            {hist.length === 0 && <p className="text-sm text-neutral-500 text-center py-8">Sem histórico.</p>}
            <div className="divide-y divide-neutral-100">
              {hist.map(p => (
                <div key={p.id} className="p-3 flex items-center justify-between text-sm">
                  <div className="flex-1 min-w-0">
                    <b>#{p.numero}</b> · {p.cliente_nome}
                    <div className="text-xs text-neutral-500">{new Date(p.created_at).toLocaleString("pt-BR")} · {p.forma_pagamento || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-[#EA1D2C]">{money(p.total)}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "entregue" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{p.status === "entregue" ? "Entregue" : "Em rota"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
